import {
  Calendar,
  CheckSquare,
  ChevronsLeft,
  ChevronsRight,
  FileBarChart,
  FileText,
  Inbox,
  LayoutDashboard,
  LogOut,
  Scale,
  Settings,
  UploadCloud,
  Users,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  type SidebarState,
  writeSidebarCookieClient,
} from '@/lib/sidebar-cookie';
import { useAuthStore } from '@/lib/auth-store';
import { useBranding } from '@/lib/use-branding';
import { usePermissions } from '@/lib/use-permissions';
import type { AppModule } from '@/lib/use-permissions';
import { useTour } from '@/lib/tour/tour-provider';
import { TOURS } from '@/lib/tour/tour-registry';
import type { TourKey } from '@proadv/types';

type NavItem = {
  href: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
  /** Modulo exigido para exibir o item. Undefined = sempre visivel. */
  module?: AppModule;
  /** Se true, somente owner/admin verao o item. */
  adminOnly?: boolean;
  /** Preenchido em runtime: tour do modulo (indicador + clique inicia). */
  tourKey?: TourKey;
  tourPending?: boolean;
};

/** Key do tour cujo `route` bate exatamente com o href do item, se houver. */
function tourKeyForHref(href: string): TourKey | null {
  for (const tour of Object.values(TOURS)) {
    if (tour.route === href) return tour.key;
  }
  return null;
}

const PRIMARY_ITEMS: NavItem[] = [
  { href: '/escritorio', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/escritorio/clientes', label: 'Clientes', icon: Users, module: 'clients' },
  {
    href: '/escritorio/portal/caixa-entrada',
    label: 'Caixa do portal',
    icon: UploadCloud,
    module: 'clients',
  },
  { href: '/escritorio/processos', label: 'Processos', icon: Scale, module: 'cases' },
  { href: '/escritorio/intimacoes', label: 'Intimações', icon: Inbox, module: 'publications' },
  { href: '/escritorio/atividades', label: 'Atividades', icon: CheckSquare, module: 'agenda' },
  { href: '/escritorio/agenda', label: 'Agenda', icon: Calendar, module: 'agenda' },
  { href: '/escritorio/documentos', label: 'Documentos', icon: FileText, module: 'documents' },
  { href: '/escritorio/financeiro', label: 'Financeiro', icon: Wallet, module: 'financial' },
  { href: '/escritorio/relatorios', label: 'Relatórios', icon: FileBarChart, module: 'reports' },
];

const SECONDARY_ITEMS: NavItem[] = [
  {
    href: '/escritorio/configuracoes',
    label: 'Configurações',
    icon: Settings,
    adminOnly: true,
  },
];

interface SidebarProps {
  initialState: SidebarState;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  onToggle?: (state: SidebarState) => void;
}

export function Sidebar({
  initialState,
  mobileOpen,
  onCloseMobile,
  onToggle,
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const signOut = useAuthStore((s) => s.signOut);
  const [state, setState] = useState<SidebarState>(initialState);

  function toggle() {
    const next: SidebarState = state === 'expanded' ? 'collapsed' : 'expanded';
    setState(next);
    writeSidebarCookieClient(next);
    onToggle?.(next);
  }

  async function handleSignOut() {
    await signOut();
    navigate('/sign-in', { replace: true });
  }

  const collapsed = state === 'collapsed';
  const { branding } = useBranding();
  const { isAdmin, hasModule } = usePermissions();
  const { isTourPending, requestTour } = useTour();
  const [intimNotRead, setIntimNotRead] = useState<number | null>(null);
  const [portalUploadsPending, setPortalUploadsPending] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .intimacoesStatsSummary()
      .then((s) => {
        if (!cancelled) setIntimNotRead(s.totalNotRead);
      })
      .catch(() => {
        if (!cancelled) setIntimNotRead(null);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .portalUploadsList({ status: 'pending', page: 1, pageSize: 1 })
      .then((res) => {
        if (!cancelled) setPortalUploadsPending(res.total);
      })
      .catch(() => {
        if (!cancelled) setPortalUploadsPending(null);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const primaryItemsWithBadges = PRIMARY_ITEMS
    .filter((item) => {
      if (item.module && !hasModule(item.module)) return false;
      return true;
    })
    .map((item) => {
      let next = item;
      if (item.href === '/escritorio/intimacoes' && intimNotRead && intimNotRead > 0) {
        next = { ...next, badge: intimNotRead };
      }
      if (
        item.href === '/escritorio/portal/caixa-entrada' &&
        portalUploadsPending &&
        portalUploadsPending > 0
      ) {
        next = { ...next, badge: portalUploadsPending };
      }
      const tourKey = tourKeyForHref(item.href);
      if (tourKey) {
        next = { ...next, tourKey, tourPending: isTourPending(tourKey) };
      }
      return next;
    });

  const secondaryItemsFiltered = SECONDARY_ITEMS
    .filter((item) => {
      if (item.adminOnly && !isAdmin) return false;
      return true;
    })
    .map((item) => {
      const tourKey = tourKeyForHref(item.href);
      return tourKey ? { ...item, tourKey, tourPending: isTourPending(tourKey) } : item;
    });

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity md:hidden',
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onCloseMobile}
        aria-hidden
      />

      {/*
        Botão de toggle externo: visível apenas quando sidebar está colapsado
        E há logo compacta configurada. Fica fixado na borda direita do sidebar
        (left-16 = 64px = largura do sidebar colapsado), no topo da página,
        com z-index acima do conteúdo principal.
      */}
      {collapsed && branding?.brandingEnabled && branding.collapsedLogoUrl ? (
        <button
          type="button"
          onClick={toggle}
          aria-label="Expandir menu"
          className={cn(
            'fixed top-4 z-[60] hidden md:flex',
            'h-8 w-4 items-center justify-center',
            'rounded-r-md border border-l-0 border-[var(--border)]',
            'bg-[var(--surface)] text-[var(--fg-muted)]',
            'hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
            'transition-colors',
          )}
          style={{ left: '64px' }}
        >
          <ChevronsRight className="h-3 w-3" />
        </button>
      ) : null}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col transition-[width,transform] duration-200 ease-out',
          // Mesma declaracao de bg/blur do topbar (ver topbar.tsx) —
          // identica nos dois em vez de aproximada, entao a cor bate "por
          // construcao" nos 3 temas. Precisa ser incondicional (nao so
          // desktop): no mobile a sidebar vira overlay por cima do
          // CONTEUDO (que tem fundo proprio opaco/gradiente) — sem pintura
          // propria, o conteudo vazaria por baixo do menu.
          'bg-[var(--surface)] backdrop-blur-[var(--blur)]',
          collapsed ? 'w-16' : 'w-60',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          'md:translate-x-0',
        )}
        aria-label="Navegação principal"
      >
        <div className="flex h-16 items-center justify-between px-3">
          {/* Quando colapsado e com logo compacta: exibe a logo no lugar do botão interno */}
          {collapsed && branding?.brandingEnabled && branding.collapsedLogoUrl ? (
            <Link
              to="/escritorio"
              className="flex w-full items-center justify-center"
              title="Dashboard"
            >
              <img
                src={branding.collapsedLogoUrl}
                alt="Logo"
                className="logo-shadow-light"
                style={{
                  width: branding.collapsedLogoSizePx ?? 32,
                  height: branding.collapsedLogoSizePx ?? 32,
                  objectFit: 'contain',
                }}
              />
            </Link>
          ) : null}

          {!collapsed && (
            <Link
              to="/escritorio"
              className="flex min-w-0 flex-1 items-center gap-2 px-2"
            >
              {branding?.brandingEnabled && branding.headerLogoUrl ? (
                <>
                  <img
                    src={branding.headerLogoUrl}
                    alt={branding.headerName ?? 'Logo'}
                    style={{ height: branding.headerLogoSizePx ?? 32 }}
                    className="logo-shadow-light max-w-full shrink-0 object-contain"
                  />
                  {branding.headerName && (
                    <span
                      className="truncate text-sm font-semibold"
                      style={{ color: branding.headerTextColor ?? 'var(--fg)' }}
                    >
                      {branding.headerName}
                    </span>
                  )}
                </>
              ) : branding?.brandingEnabled && branding.headerName ? (
                <span
                  className="truncate text-lg font-semibold tracking-tight"
                  style={{ color: branding.headerTextColor ?? 'var(--fg)' }}
                >
                  {branding.headerName}
                </span>
              ) : (
                <span className="truncate text-lg font-semibold tracking-tight text-[var(--fg)]">
                  VoxisAdv
                </span>
              )}
            </Link>
          )}
          <div className={cn('flex items-center gap-1', collapsed && branding?.brandingEnabled && branding.collapsedLogoUrl ? 'hidden' : 'ml-auto')}>
            <button
              type="button"
              onClick={toggle}
              className={cn(
                'hidden h-9 w-9 items-center justify-center rounded-md md:inline-flex',
                'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
              )}
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            >
              {collapsed ? (
                <ChevronsRight className="h-4 w-4" />
              ) : (
                <ChevronsLeft className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={onCloseMobile}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] md:hidden"
              aria-label="Fechar menu"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Sem border-r: chrome (sidebar+topbar) e conteudo agora se
            separam pelo contraste de cor + canto arredondado da "dobra"
            (ver dashboard-shell.tsx), uma linha reta aqui ficava
            redundante e destoava do corte arredondado. */}
        <nav className="flex-1 overflow-y-auto px-2 py-4">
          <ul className="flex flex-col gap-1">
            {primaryItemsWithBadges.map((item) => (
              <li key={item.href}>
                <NavLink
                  item={item}
                  active={isActive(location.pathname, item.href)}
                  collapsed={collapsed}
                  onNavigate={onCloseMobile}
                  onRequestTour={requestTour}
                />
              </li>
            ))}
          </ul>

          <div className="my-4 border-t border-[var(--border)]" />

          <ul className="flex flex-col gap-1">
            {secondaryItemsFiltered.map((item) => (
              <li key={item.href}>
                <NavLink
                  item={item}
                  active={isActive(location.pathname, item.href)}
                  collapsed={collapsed}
                  onNavigate={onCloseMobile}
                  onRequestTour={requestTour}
                />
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={handleSignOut}
                className={cn(
                  'group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition',
                  'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                  collapsed && 'justify-center px-0',
                )}
                aria-label="Sair"
                title={collapsed ? 'Sair' : undefined}
              >
                <LogOut className="h-4 w-4 shrink-0" />
                {!collapsed && <span className="truncate">Sair</span>}
              </button>
            </li>
          </ul>
        </nav>
      </aside>
    </>
  );
}

function NavLink({
  item,
  active,
  collapsed,
  onNavigate,
  onRequestTour,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onNavigate: () => void;
  onRequestTour?: (key: TourKey) => void;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      onClick={() => {
        onNavigate();
        if (item.tourKey && item.tourPending) {
          onRequestTour?.(item.tourKey);
        }
      }}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? item.label : undefined}
      className={cn(
        'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition',
        'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
        active &&
          'bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] text-[var(--fg)]',
        collapsed && 'justify-center px-0',
      )}
    >
      {active && (
        <span
          className="absolute inset-y-1 left-0 w-0.5 rounded-r-full bg-[var(--accent)]"
          aria-hidden
        />
      )}
      <span className="relative shrink-0">
        <Icon className="h-4 w-4" />
        {item.tourPending && (
          <span
            className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--accent)]"
            aria-hidden
            title="Tour disponível"
          />
        )}
      </span>
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!collapsed && item.badge && item.badge > 0 ? (
        <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--accent)] px-1.5 text-[10px] font-semibold text-white">
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      ) : null}
    </Link>
  );
}

function isActive(pathname: string, href: string): boolean {
  if (href === '/escritorio') return pathname === '/escritorio';
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);
  return isMobile;
}
